import {Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {NetworkedComponent, networkManager} from '@wonderlandcloud/client';
import {Grabbable} from './wle-interaction/components/grabbable.js';
import {Interactor, HandednessValues} from './wle-interaction/components/interactor.js';
import {Interactable} from './wle-interaction/components/interactable.js';
import {PhysXComponent} from '@wonderlandengine/api';
import {Events} from './network-events.js';
import {EventsClient} from './events-client.js';
import Logger from './utils/logger.js';
import { EToyDefinition } from './etoy/etoy-types.js';

const MODULE = 'NetworkedGrabbable';

/**
 * NetworkedGrabbable component - synchronizes grabbed objects across the network
 * 
 * This component extends NetworkedComponent to add multiplayer grab functionality:
 * - When grabbed locally, it takes ownership and sends position/rotation to other clients
 * - When grabbed remotely, it receives position/rotation from the owner
 * - Automatically switches between 'send' and 'receive' modes based on ownership
 * 
 * Usage:
 * 1. Add this component to an object with a Grabbable component
 * 2. Set the networkId property (unique ID for this object across the network)
 * 3. The object will automatically synchronize when grabbed/released
 * 
 * @example
 * // In Wonderland Editor:
 * // - Add Interactable component to object
 * // - Add Grabbable component with handle set
 * // - Add NetworkedGrabbable component with unique networkId and mode='receive'
 */
export class NetworkedGrabbable extends NetworkedComponent {
    static TypeName = 'networked-grabbable';
    static InheritProperties = true;
    
    @property.bool()
    unsupervisedKinematic!: boolean;

    /**
     * Reference to the Grabbable component on this object
     */
    private grabbable: Grabbable | null = null;

    /**
     * Reference to the PhysX component for kinematic control
     */
    private physx: PhysXComponent | null = null;

    /**
     * Current owner's player ID (null if not grabbed)
     */
    private currentOwner: number | null = null;

    /**
     * Local player's network ID (retrieved from EventsClient)
     */
    private get localPlayerId(): number {
        return EventsClient.instance?.playerUserId ?? -1;
    }

    /**
     * Whether this object is currently grabbed locally
     */
    public isGrabbedLocally: boolean = false;

    /**
     * Original parent before grab (to restore on release)
     */
    private originalParent: Object3D | null = null;

    /**
     * Whether this component has already been registered with EventsClient
     */
    private registeredWithEventsClient: boolean = false;

    /**
     * Stored position to preserve during release
     */
    private releasePosition: Float32Array | null = null;
    private releaseRotation: Float32Array | null = null;

    /**
     * Re-enable Grabbable component so it can process release events
     * Should be called just before releasing the grab
     */
    public prepareForRelease() {
        // Capture current world transform before re-enabling Grabbable
        this.releasePosition = new Float32Array(this.object.getPositionWorld());
        this.releaseRotation = new Float32Array(this.object.getRotationWorld());
        
        if (this.grabbable) {
            this.grabbable.active = true;
        }
    }

    override start() {
        // Get reference to PhysX component if present
        this.physx = this.object.getComponent(PhysXComponent);
        if (this.physx) {
            this.physx.kinematic = true; // Start as kinematic until network initialized
        }

        // Only call parent start() if we have a valid networkId
        if (this.networkId !== -1) {
            super.start();
        }

        // Get reference to Grabbable component
        this.grabbable = this.object.getComponent(Grabbable);
        if (!this.grabbable) {
            Logger.error(MODULE, `NetworkedGrabbable requires a Grabbable component on ${this.object.name}`);
            return;
        }
    }

    /**
     * Complete initialization after receiving networkId from server
     * Called by EventsClient when BALL_INFO arrives
     */
    completeInitialization(physxOwnership: boolean) {
        Logger.debug("NetworkedGrabbable completeInitialization called for id: " + this.networkId, MODULE);
        if (this.networkId !== -1) {
            super.start();

            const physxComp = this.object.getComponent('physx');
            if (physxComp) {
                physxComp.active = true;
            }
            
            // onActivate will be called by the engine and handle registration
            // Just set up event listeners here
            if (this.grabbable) {
                this.grabbable.onGrabStart.add(this.onLocalGrabStart);
                this.grabbable.onGrabEnd.add(this.onLocalGrabEnd);
            }
            
            // EventsClient will call registerNetworkedGrabbable() after this method returns
            // Mark that we've already registered to prevent double registration in onActivate()
            this.registeredWithEventsClient = true;
            
            // Register with networkManager
            networkManager.registerNetworkedComponent(this, physxOwnership);
        }
    }

    override onActivate() {
        // For objects with pre-assigned networkId, set up normally
        // For dynamically assigned networkId (ball), completeInitialization() handles setup
        if (this.networkId !== -1) {
            networkManager.registerNetworkedComponent(this, false); // false = receive mode
            
            // Subscribe to grab/release events
            if (this.grabbable) {
                this.grabbable.onGrabStart.add(this.onLocalGrabStart);
                this.grabbable.onGrabEnd.add(this.onLocalGrabEnd);
            }

            // Only register with EventsClient if not already done by completeInitialization
            if (!this.registeredWithEventsClient && EventsClient.instance) {
                EventsClient.instance.registerNetworkedGrabbable(this.networkId, this);
                this.registeredWithEventsClient = true;
            }
        }
    }

    override onDeactivate() {
        // Clean up event listeners
        if (this.grabbable) {
            this.grabbable.onGrabStart.remove(this.onLocalGrabStart);
            this.grabbable.onGrabEnd.remove(this.onLocalGrabEnd);
        }

        // Unregister from EventsClient
        if (EventsClient.instance) {
            EventsClient.instance.unregisterNetworkedGrabbable(this.networkId);
        }

        // Call parent to unregister from networkManager
        super.onDeactivate();
    }

    /**
     * Called when the object is grabbed locally
     */
    private onLocalGrabStart = (interactor: Interactor, interactable: Interactable, grabbable: Grabbable) => {
        const handStr = HandednessValues[interactor.handedness] || 'unknown';
        Logger.debug(`[DEBUG] grab-start id=${this.networkId} hand=${handStr} obj='${this.object.name}' interactor='${interactor.object.name}'`);
        
        // Prevent grabbing if another user already owns this toy
        const toy = EventsClient.instance?.toys.get(this.networkId);
        const currentOwner = toy?.currentGrabbedBy ?? null;
        if (toy && currentOwner !== null && currentOwner !== this.localPlayerId) {
            Logger.warn(MODULE, `[GRAB] Object ${this.networkId} already grabbed by user ${currentOwner}, ignoring local grab.`);
            this.grabbable?.release(interactor, interactable);
            return;
        }

        this.isGrabbedLocally = true;
        this.currentOwner = this.localPlayerId;

        if (toy) {
            toy.currentGrabbedBy = this.localPlayerId;
            EventsClient.instance?.toys.set(this.networkId, toy);
        }
        
        this.managePhysxOwnership(toy);

        // Notify other clients that we grabbed this object
        if (networkManager && networkManager.client) {
            networkManager.client.sendViaWs({
                type: Events.OBJECT_GRAB,
                data: {
                    objectId: this.networkId,
                    playerId: this.localPlayerId,
                    grabbed: true,
                },
            });
        }
        // The interactor's object is what's grabbing (VR controller or virtual hand/camera in non-VR)
        const playerObject = interactor.object;
        
        if (playerObject) {
            // Store original parent
            this.originalParent = this.object.parent;

            // Capture world transform BEFORE reparenting as typed arrays (copies, not references)
            const ballWorldPos = new Float32Array(this.object.getPositionWorld());
            const ballWorldRot = new Float32Array(this.object.getRotationWorld());
            const ballWorldScale = new Float32Array(this.object.getScalingWorld());
            
            // Parent the ball to the interactor
            this.object.parent = playerObject;

            // Defer transform restoration to next frame to avoid race with Wonderland's internal parenting updates
            setTimeout(() => {
                // Restore world transform after reparenting so the object stays in the same world transform
                this.object.setPositionWorld(ballWorldPos);
                this.object.setRotationWorld(ballWorldRot);
                this.object.setScalingWorld(ballWorldScale);
                
                // Suppress Grabbable's update loop so it doesn't override parent-child positioning
                if (this.grabbable && 'setSuppressUpdate' in this.grabbable) {
                    (this.grabbable as any).setSuppressUpdate(true);
                }
            }, 0);


            // Keep Grabbable active so its release handler still fires.
            // We previously disabled it here, which stopped onSelectEnd → release from running.
            // Parenting the object to the interactor already gives us the desired motion; the
            // Grabbable update is redundant but harmless, and events remain intact.
            if (this.grabbable) {
                this.grabbable.active = true;
            }

            const p = Array.from(this.object.getPositionWorld()).map(v => v.toFixed(2)).join(',');
            Logger.debug(`[TRACE] grab-parented id=${this.networkId} parent='${this.object.parent?.name ?? 'SCENE_ROOT'}' pos=[${p}]`, MODULE);
        } else {
            Logger.warn(MODULE, 'Interactor object is null!');
        }
    };

    /**
     * Called when the object is released locally
     */
    private onLocalGrabEnd = (interactor: Interactor, interactable: Interactable, grabbable: Grabbable) => {
        // Re-enable Grabbable updates so release() and onSelectEnd processing run
        if (this.grabbable && 'setSuppressUpdate' in this.grabbable) {
            (this.grabbable as any).setSuppressUpdate(false);
        }
        const handStrEnd = HandednessValues[interactor.handedness] || 'unknown';
        Logger.debug(`[TRACE] grab-end id=${this.networkId} hand=${handStrEnd} obj='${this.object.name}' interactor='${interactor.object.name}'`, MODULE);
        
        this.isGrabbedLocally = false;
        this.currentOwner = null;

        const toy = EventsClient.instance?.toys.get(this.networkId);
        if (toy) {
            toy.currentGrabbedBy = null;
            EventsClient.instance?.toys.set(this.networkId, toy);
        }

        // Capture world transform BEFORE unparenting so we can restore it after
        const worldPos = this.object.getPositionWorld(new Float32Array(3));
        const worldRot = this.object.getRotationWorld(new Float32Array(4));
        const worldScale = this.object.getScalingWorld(new Float32Array(3));

        // Always explicitly set parent to null to break parent-child relationship
        // This effectively parents to scene root
        this.object.parent = null;
        this.originalParent = null;

        // Defer transform restoration to next frame to avoid race with Wonderland's internal parenting updates
        setTimeout(() => {
            // Restore world transform after unparenting to prevent coordinate space jump
            this.object.setPositionWorld(worldPos);
            this.object.setRotationWorld(worldRot);
            this.object.setScalingWorld(worldScale);
            
        }, 0);

        // Capture final transform after unparenting and restoring
        const finalTransform = new Float32Array(this.object.transformWorld);

        const p = Array.from(this.object.getPositionWorld()).map(v => v.toFixed(2)).join(',');
        Logger.debug(`[TRACE] released id=${this.networkId} parent='${this.object.parent ?? 'SCENE_ROOT'}' pos=[${p}]`, MODULE);
        Logger.debug("Evaluating physx ownership for toy:" + this.localPlayerId + " " + toy.physxOwner, MODULE);

        this.managePhysxOwnership(toy);

        // Notify other clients of release with final position
        if (networkManager && networkManager.client) {
            networkManager.client.sendViaWs({
                type: Events.OBJECT_GRAB,
                data: {
                    objectId: this.networkId,
                    playerId: this.localPlayerId,
                    grabbed: false,
                    finalTransform: Array.from(finalTransform),
                },  
            });
        }
    };

    /**
     * Called from EventsClient when another player grabs/releases this object
     * 
     * @param playerId - The player who grabbed/released the object
     * @param grabbed - True if grabbed, false if released
     */
    onRemoteGrabChange(playerId: number, grabbed: boolean) {
        const toy = EventsClient.instance?.toys.get(this.networkId);

        if (grabbed) {
            // Another player grabbed this object
            this.currentOwner = playerId;
            
            // Disable local grabbable to prevent conflicts
            if (this.grabbable) {
                this.grabbable.active = false;
            }
        } else {
            // Another player released this object
            if (this.currentOwner === playerId) {
                this.currentOwner = null;
            }
            
            // Re-enable local grabbable
            if (this.grabbable) {
                this.grabbable.active = true;
            }
        }

        this.managePhysxOwnership(toy);
    }

    public managePhysxOwnership(toy: EToyDefinition) {
        const physxOwnership = (toy.physxOwner === this.localPlayerId);
        if (toy.networkId !== -1) {
            networkManager.removeObject(toy.networkId);

            if (this.isGrabbedLocally) {
                networkManager.registerNetworkedComponent(this, true);
                this.mode = 'send';
            } else if (toy.currentGrabbedBy !== null) {
                networkManager.registerNetworkedComponent(this, false);
                this.mode = 'receive';
            } else {
                networkManager.registerNetworkedComponent(this, physxOwnership);
                this.mode = physxOwnership ? 'send' : 'receive';
            }
        }

        const physxComp = this.object.getComponent('physx');
        if (physxComp) {
            Logger.debug("Setting kinematic state:" + this.isGrabbedLocally + " " + this.currentOwner + " " + physxOwnership, MODULE);
            if (this.isGrabbedLocally || this.currentOwner !== null || !physxOwnership) {
                physxComp.kinematic = true;
            } else if (this.grabbable) {
                this.grabbable.physxOnTimeout = true;
                setTimeout(() => {
                    Logger.debug("Restoring original kinematic state:" + this.unsupervisedKinematic, MODULE);
                    physxComp.kinematic = this.unsupervisedKinematic;
                    if(this.grabbable) {
                        this.grabbable.physxOnTimeout = false;
                    }
                }, 100);
            }
        }
    }

    /**
     * Check if this object is currently owned by any player
     */
    get isOwned(): boolean {
        return this.currentOwner !== null;
    }

    /**
     * Check if this object is owned by the local player
     */
    get isOwnedLocally(): boolean {
        return this.currentOwner === this.localPlayerId;
    }

    /**
     * Get the current owner's player ID
     */
    get owner(): number | null {
        return this.currentOwner;
    }
}
